/**
 * Service API pour les utilisateurs
 */

import { User } from '../../types';
import { UserLoginSchema, UserCreateSchema, validateData } from '../../validation';
import { apiCall } from './client';
import { logger } from '../logger';

export const usersApi = {
  /**
   * Se connecter
   */
  async login(email: string, password: string): Promise<User & { token: string; refreshToken?: string }> {
    try {
      const validated = validateData(
        UserLoginSchema,
        { email, password },
        'usersApi.login'
      );

      const result = await apiCall<User & { token: string; refreshToken?: string }>('POST', 'index.php?action=login', {
        email: validated.email,
        password: validated.password,
      });

      if (!result) throw new Error('Login failed');

      sessionStorage.setItem('orion_access_token', result.token);
      // Le refresh token est géré côté serveur via un cookie httpOnly (Secure, SameSite=Strict).
      // Aucune persistance côté JS pour empêcher le vol via XSS.
      logger.setUser(result.id, result.email, result.companyId);
      logger.info('[Auth] Login successful', { userId: result.id, email: result.email });

      return result;
    } catch (error) {
      logger.error('[Auth] Login failed', error as Error);
      throw error;
    }
  },

  /**
   * Se déconnecter
   */
  async logout(reason: 'manual' | 'inactivity' = 'manual'): Promise<void> {
    const token = sessionStorage.getItem('orion_access_token');
    const sessionId = sessionStorage.getItem('orion_heartbeat_session_id');
    // Supprimer le token d'accès local
    sessionStorage.removeItem('orion_access_token');
    sessionStorage.removeItem('orion_heartbeat_session_id');
    // Nettoyage rétrocompat : anciennes clés localStorage potentiellement persistantes
    localStorage.removeItem('auth_token');
    localStorage.removeItem('orion_crm_refresh_token');
    // Envoyer le logout au serveur via XMLHttpRequest synchrone pour garantir l'envoi
    if (token) {
      try {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', '/api/index.php?action=logout', false); // synchrone
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.send(JSON.stringify({ reason, session_id: sessionId }));
      } catch {}
    }
    logger.clearUser();
    logger.info('[Auth] Logout successful');
  },

  /**
   * Récupérer utilisateur courant
   */
  async getCurrentUser(): Promise<User> {
    try {
      const result = await apiCall<User>('GET', 'index.php?action=me');
      if (!result) throw new Error('Current user not found');
      return result;
    } catch (error) {
      logger.warn('[Users] Failed to fetch current user', error);
      throw error;
    }
  },

  /**
   * Récupérer tous les utilisateurs (admin)
   */
  async getAllUsers(): Promise<User[]> {
    try {
      const result = await apiCall<User[]>('GET', 'index.php?action=getUsers');
      return result || [];
    } catch (error) {
      logger.warn('[Users] Failed to fetch all users', error);
      return [];
    }
  },

  /**
   * Créer utilisateur
   */
  async createUser(userData: Omit<User, 'id'>): Promise<User> {
    try {
      const validated = validateData(
        UserCreateSchema,
        { id: `u_${Date.now()}`, ...userData },
        'usersApi.createUser'
      );

      const result = await apiCall<User>('POST', 'index.php?action=createUser', validated);

      if (!result) throw new Error('User creation failed');

      logger.info('[Users] User created', { userId: result.id, email: result.email });
      return result;
    } catch (error) {
      logger.error('[Users] Failed to create user', error as Error);
      throw error;
    }
  },

  /**
   * Mettre à jour utilisateur
   */
  async updateUser(userId: string, updates: Partial<User>): Promise<User> {
    try {
      const result = await apiCall<User>(
        'PUT',
        `index.php?action=updateUser&id=${userId}`,
        updates
      );

      if (!result) throw new Error('User update failed');

      logger.info('[Users] User updated', { userId });
      return result;
    } catch (error) {
      logger.error('[Users] Failed to update user', error as Error, { userId });
      throw error;
    }
  },

  /**
   * Supprimer utilisateur
   */
  async deleteUser(userId: string): Promise<boolean> {
    try {
      await apiCall<void>('DELETE', `index.php?action=deleteUser&id=${userId}`);
      logger.info('[Users] User deleted', { userId });
      return true;
    } catch (error) {
      logger.error('[Users] Failed to delete user', error as Error, { userId });
      throw error;
    }
  },

  /**
   * Faire l'impersonation d'un utilisateur (admin)
   */
  async impersonateUser(userId: string): Promise<User> {
    try {
      const result = await apiCall<User>('POST', 'index.php?action=impersonateUser', {
        userId,
      });

      if (!result) throw new Error('Impersonation failed');

      logger.info('[Users] Impersonation started', { impersonatedUserId: userId });
      return result;
    } catch (error) {
      logger.error('[Users] Failed to impersonate user', error as Error, { userId });
      throw error;
    }
  },
};
